import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Save, Server, Link, Shield, RefreshCw, Code, DownloadCloud, UploadCloud, Copy, CheckCircle2, Terminal, Trash2 } from "lucide-react";
import { REACT_APP_API_URL } from "config/api-config";

export default function HRMSSetup({ onClose }) {
  const [activeTab, setActiveTab] = useState("pull"); // "pull" or "push"
  const [loading, setLoading] = useState(false);
  
  // States for Pull API (CamSec -> HRMS)
  const [pullApiKey, setPullApiKey] = useState("Loading...");
  const [resettingKey, setResettingKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);

  // States for Push Webhook (HRMS <- CamSec)
  const [syncing, setSyncing] = useState(false);
  const [formData, setFormData] = useState({ webhook_url: "", api_key: "", auto_sync: true });
  const [savedData, setSavedData] = useState({ webhook_url: "", api_key: "", auto_sync: true });

  // Get Dynamic URL for the Pull API based on current domain
  const getDynamicApiUrl = () => {
    const currentHost = window.location.hostname;
    if (currentHost === "localhost" || currentHost === "127.0.0.1") return `${REACT_APP_API_URL}/events/hrms/pull_attendance`;
    const baseUrl = REACT_APP_API_URL.includes("/api") ? `${window.location.origin}/api` : window.location.origin;
    return `${baseUrl}/events/hrms/pull_attendance`;
  };
  const pullApiUrl = getDynamicApiUrl();

  useEffect(() => {
    fetchPullSettings();
    fetchPushConfig();
  }, []);

  // Fetch Pull API Key
  const fetchPullSettings = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${REACT_APP_API_URL}/events/hrms/pull_settings`, { headers: { Authorization: `Bearer ${token}` } });
      setPullApiKey(res.data.api_key);
    } catch (error) { toast.error("Failed to load Pull API settings."); }
  };

  // Fetch Push Webhook Config
  const fetchPushConfig = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${REACT_APP_API_URL}/events/hrms/config`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) {
        const fetchedConfig = { webhook_url: res.data.webhook_url || "", api_key: res.data.api_key || "", auto_sync: res.data.auto_sync !== false };
        setFormData(fetchedConfig);
        setSavedData(fetchedConfig);
      }
    } catch (error) {}
  };

  const handleResetPullKey = async () => {
    if(!window.confirm("Are you sure? This will instantly break any existing Pull API connections.")) return;
    setResettingKey(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/hrms/pull_settings/reset_key`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPullApiKey(res.data.api_key); 
      toast.success("New API key generated!");
    } catch (error) { toast.error("Failed to reset API key."); } finally { setResettingKey(false); }
  };

  const handleSavePush = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${REACT_APP_API_URL}/events/hrms/config`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Push Configuration Saved!");
      setSavedData(formData);
    } catch (error) { toast.error("Failed to save configuration."); } finally { setLoading(false); }
  };

  const handleManualPushSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${REACT_APP_API_URL}/events/hrms/manual_sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Successfully pushed ${res.data.synced_count} records to your HRMS!`);
    } catch (error) { toast.error(error.response?.data?.detail || "Manual sync failed."); } finally { setSyncing(false); }
  };

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const curlCommand = `curl -X GET "${pullApiUrl}?date=2026-09-06" \\
-H "x-api-key: ${pullApiKey}"`;

  const hasUnsavedChanges = JSON.stringify(formData) !== JSON.stringify(savedData);
  const isUrlEmpty = formData.webhook_url.trim() === "";

  return (
    <div style={{ backgroundColor: "#FFFFFF", borderRadius: "24px", overflow: "hidden", boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", maxHeight: "85vh" }}>
      
      <div style={{ backgroundColor: "#F4F7FE", padding: "24px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "#4318FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Server size={24} color="white" />
          </div>
          <div>
            <h3 style={{ margin: 0, color: "#2B3674", fontSize: "20px", fontWeight: "700" }}>HRMS Integration</h3>
            <p style={{ margin: 0, color: "#A3AED0", fontSize: "14px" }}>Connect your software to CamSec Ai</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#A3AED0", transition: "0.2s" }} onMouseOver={(e) => e.target.style.color = "#2B3674"} onMouseOut={(e) => e.target.style.color = "#A3AED0"}>
          <X size={24} />
        </button>
      </div>

      <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0", backgroundColor: "#FAFCFE", padding: "0 30px" }}>
        <button 
          onClick={() => setActiveTab("pull")}
          style={{ padding: "16px 20px", background: "none", border: "none", borderBottom: activeTab === "pull" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "pull" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <DownloadCloud size={18} /> Option 1: Pull API (Recommended)
        </button>
        <button 
          onClick={() => setActiveTab("push")}
          style={{ padding: "16px 20px", background: "none", border: "none", borderBottom: activeTab === "push" ? "3px solid #4318FF" : "3px solid transparent", color: activeTab === "push" ? "#4318FF" : "#A3AED0", fontWeight: "700", fontSize: "15px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "0.2s" }}
        >
          <UploadCloud size={18} /> Option 2: Push Webhook
        </button>
      </div>

      <div style={{ padding: "30px", overflowY: "auto", flex: 1 }}>
        
        {/* === PULL API TAB === */}
        {activeTab === "pull" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.3s ease-out" }}>
            
            <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #4318FF" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How the Pull API Works</h4>
              <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
                Give the URL and API Key below to your IT Developer. Their software can make a simple <code>GET</code> request to this endpoint whenever they want, and CamSec Ai will return all Staff and Student attendance records for that day.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Link size={16} color="#4318FF"/> CamSec API Endpoint
              </label>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <code style={{ flexGrow: 1, backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", padding: "12px", borderRadius: "8px", color: "#4318FF", fontSize: "14px", fontWeight: "600", wordBreak: "break-all" }}>
                  {pullApiUrl}
                </code>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Shield size={16} color="#4318FF"/> Your API Key
              </label>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                <input 
                  type="text" 
                  readOnly 
                  value={pullApiKey} 
                  style={{ flexGrow: 1, minWidth: "250px", padding: "12px", borderRadius: "8px", border: "1px solid #E2E8F0", fontSize: "14px", backgroundColor: "#FFF5F5", color: "#E53E3E", fontWeight: "600", letterSpacing: "1px" }} 
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => copyToClipboard(pullApiKey, setCopiedKey)} style={{ padding: "12px 16px", backgroundColor: "#F4F7FE", color: "#4318FF", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>
                    {copiedKey ? "Copied" : "Copy"}
                  </button>
                  <button onClick={handleResetPullKey} disabled={resettingKey} style={{ padding: "12px 16px", backgroundColor: "#ffeeec", color: "#EE5D50", border: "none", borderRadius: "8px", cursor: resettingKey ? "not-allowed" : "pointer", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                    <RefreshCw size={16} className={resettingKey ? "animate-spin" : ""} />
                    {resettingKey ? "Resetting..." : "Reset Key"}
                  </button>
                </div>
              </div>
            </div>

            {/* cURL COMMAND BLOCK */}
            <div style={{ backgroundColor: "#0B1437", padding: "16px", borderRadius: "12px", border: "1px solid #2B3674", marginTop: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#A3AED0", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Terminal size={14} /> HOW TO CALL THE API (cURL)
                </p>
                <button onClick={() => copyToClipboard(curlCommand, setCopiedCurl)} style={{ background: "none", border: "none", color: "#00E396", cursor: "pointer", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                  {copiedCurl ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                  {copiedCurl ? "COPIED" : "COPY CMD"}
                </button>
              </div>
              <p style={{ margin: "0 0 10px 0", color: "#8F9BBA", fontSize: "12px" }}>The <code>date</code> parameter is optional. If omitted, it returns today's data.</p>
              <pre style={{ margin: 0, padding: "12px", backgroundColor: "#111C44", color: "#00E396", borderRadius: "8px", fontSize: "13px", fontFamily: "monospace", overflowX: "auto" }}>
                {curlCommand}
              </pre>
            </div>
          </div>
        )}

        {/* === PUSH WEBHOOK TAB === */}
        {activeTab === "push" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", animation: "fadeIn 0.3s ease-out" }}>
            
            <div style={{ backgroundColor: "#F8FAFC", padding: "20px", borderRadius: "12px", borderLeft: "4px solid #FF912C" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#2B3674", fontSize: "16px", fontWeight: "700" }}>How the Push Webhook Works</h4>
              <p style={{ margin: "0", color: "#475467", fontSize: "14px", lineHeight: "1.6" }}>
                Instead of your software asking for data, <b>we send it to you</b>. Provide a Webhook URL below, and CamSec Ai will automatically push the JSON attendance data to your server every day, or in real-time.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Link size={16} color="#4318FF"/> Destination Webhook URL (Your Server)
              </label>
              <input 
                type="text" 
                placeholder="https://your-company.com/api/attendance/webhook"
                value={formData.webhook_url}
                onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", color: "#2B3674", backgroundColor: "#F8FAFC" }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ fontSize: "14px", fontWeight: "700", color: "#2B3674", display: "flex", alignItems: "center", gap: "6px" }}>
                <Shield size={16} color="#4318FF"/> Your API Key / Bearer Token (Optional)
              </label>
              <input 
                type="password" 
                placeholder="Token required by your HRMS to accept data"
                value={formData.api_key}
                onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0", outline: "none", fontSize: "14px", boxSizing: "border-box", color: "#2B3674", backgroundColor: "#F8FAFC" }}
              />
            </div>

            <div style={{ display: "flex", gap: "15px", marginTop: "10px" }}>
              <button 
                onClick={handleManualPushSync}
                disabled={syncing || isUrlEmpty}
                style={{ flex: 1, padding: "14px", backgroundColor: isUrlEmpty ? "#F4F7FE" : "#fff8f1", color: isUrlEmpty ? "#A3AED0" : "#FF912C", border: isUrlEmpty ? "1px solid #E2E8F0" : "1px solid #ffe4cc", borderRadius: "12px", fontWeight: "700", cursor: isUrlEmpty ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "0.2s" }}
              >
                <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Pushing Data..." : "Manual Push Sync Now"}
              </button>
              
              <button 
                onClick={handleSavePush}
                disabled={loading || (!hasUnsavedChanges && !isUrlEmpty)}
                style={{ flex: 1, padding: "14px", backgroundColor: (!hasUnsavedChanges && !isUrlEmpty) ? "#A3AED0" : "#4318FF", color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: (loading || (!hasUnsavedChanges && !isUrlEmpty)) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "0.2s" }}
              >
                <Save size={18} />
                {loading ? "Saving..." : !hasUnsavedChanges && !isUrlEmpty ? "Config Saved" : "Save Webhook Config"}
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
